import { ICustomer } from 'dto/users.dto'

/**
 * Экранирует текст для HTML-ячейки Excel.
 * @param value — исходное значение
 */
const escapeHtmlCell = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Форматирует дату для Excel.
 * @param value — ISO-строка или null
 */
const formatExcelDateTime = (value?: string | null): string => {
  if (!value) {
    return ''
  }
  const dateValue = new Date(value)
  if (Number.isNaN(dateValue.getTime())) {
    return ''
  }
  return dateValue.toLocaleString('ru-RU')
}

/**
 * Форматирует сумму для Excel.
 * @param value — число
 */
const formatExcelMoney = (value?: number | null): string => {
  const amount = typeof value === 'number' ? value : 0
  return String(amount)
}

/**
 * Собирает HTML-таблицу клиентов для выгрузки в .xls.
 * @param customers — полный список строк
 */
export const buildCustomersExcelTableHtml = (customers: ICustomer[]): string => {
  const headerCells = [
    'id',
    'Имя',
    'Фамилия',
    'Тел.',
    'Email',
    'Город',
    'Пиццерия',
    'Заказов',
    'Неподтверждённая',
    'Подтверждённая',
    'Регистрация',
    'Последний заказ',
  ]
    .map(label => `<th>${escapeHtmlCell(label)}</th>`)
    .join('')

  const bodyRows = customers
    .map(customer => {
      const cells = [
        String(customer?.id ?? ''),
        customer?.first_name ?? '',
        customer?.last_name ?? '',
        customer?.phone ?? '',
        customer?.email ?? '',
        customer?.city ?? '',
        customer?.pizzeria_label ?? '',
        String(customer?.orders_count ?? 0),
        formatExcelMoney(customer?.orders_total_sum),
        formatExcelMoney(customer?.orders_confirmed_sum),
        formatExcelDateTime(customer?.create_at),
        formatExcelDateTime(customer?.last_order_at),
      ]
        .map(cellValue => `<td>${escapeHtmlCell(cellValue)}</td>`)
        .join('')

      return `<tr>${cells}</tr>`
    })
    .join('')

  return `<!DOCTYPE html><html><head><meta charset="UTF-8" /></head><body><table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table></body></html>`
}

/**
 * Скачивает список клиентов как .xls (HTML-таблица, открывается в Excel).
 * @param customers — полный список по фильтрам
 * @param filename — имя файла без расширения
 */
export const downloadCustomersExcel = (
  customers: ICustomer[],
  filename: string,
): void => {
  const safeFilename = filename?.trim?.() || 'Users'
  const tableHtml = buildCustomersExcelTableHtml(customers)
  const blob = new Blob([tableHtml], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  })
  const objectUrl = URL.createObjectURL(blob)
  const anchorElement = document.createElement('a')
  anchorElement.href = objectUrl
  anchorElement.download = `${safeFilename}.xls`
  document.body.appendChild(anchorElement)
  anchorElement.click()
  document.body.removeChild(anchorElement)
  URL.revokeObjectURL(objectUrl)
}
